import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import PageBreadcrumb from '../../Components/Common/PageBreadcrumb';
import DataTable from 'react-data-table-component';
import ActionButton from '../../Components/Common/ActionButton';
import StatusBadge from '../../Components/Common/StatusBadge';
import ListPager from '../../Components/Common/ListPager';
import Can from '../../Components/Common/Can';
import { usePermission } from '../../contexts/PermissionContext';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListSearch,
} from '../../Components/Common/ListFilters';
import { EmptyState, SkeletonRows } from '../../Components/Common/ListStates';
import notify from '../../utils/notify';
import { pick } from '../../shared/offer-domain/pick';
import '../TenderOpening/components/TenderOpening.css';
import './components/GtcView.css';
import {
  FormGrid,
  SelectField,
  TextAreaField,
  TextField,
  focusFirstError,
  hasErrors,
  required,
  validateForm,
} from '../../Components/Form';
import {
  MONTHS,
  YEARS,
  PUBLISH_TONE,
  STATUS_PUBLISH,
  STATUS_TONE,
  gtcApi,
  monthName,
  publishLabel,
} from './data/gtcApi';

const CURRENT_YEAR = String(new Date().getFullYear());
const YEAR_OPTIONS = YEARS.map(year => ({ value: year, label: year }));
const MONTH_OPTIONS = MONTHS.map((month, i) => ({
  value: String(i + 1),
  label: month,
}));

const EMPTY_FORM = {
  title: '',
  year: YEARS.includes(CURRENT_YEAR) ? CURRENT_YEAR : YEARS[YEARS.length - 1],
  month: '1',
  incotermId: null,
  description: '',
  isActive: true,
};

const GTC_SCHEMA = {
  title: { label: 'GTC Title', rules: [required()] },
};

export const GTC_TABLE_STYLES = {
  headCells: { style: { paddingLeft: '12px', paddingRight: '12px' } },
  cells: {
    style: {
      paddingLeft: '12px',
      paddingRight: '12px',
      wordBreak: 'break-word',
    },
  },
  table: { style: { width: '100%' } },
};

const GtcList = () => {
  const navigate = useNavigate();
  const { can } = usePermission();
  const canCreate = can('canCreate');
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterText, setFilterText] = useState('');
  const [search, setSearch] = useState('');
  const requestRef = useRef(0);
  const [sort, setSort] = useState({ field: null, desc: false });

  const [incoterms, setIncoterms] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  document.title = 'Master GTC';

  const loadData = useCallback(async () => {
    const reqId = ++requestRef.current;
    setIsLoading(true);
    try {
      const data = await gtcApi.list({
        PageNumber: currentPage,
        PageSize: rowsPerPage,
        Search: search || undefined,
        SortBy: sort.field || undefined,
        IsDescending: sort.field ? sort.desc : undefined,
      });
      if (reqId !== requestRef.current) return; // stale response
      setRows(pick(data, 'items', 'Items') || []);
      setTotalRows(pick(data, 'totalItems', 'TotalItems') || 0);
    } catch (err) {
      if (reqId !== requestRef.current) return;
      notify.apiError(err, 'load GTC');
      setRows([]);
      setTotalRows(0);
    } finally {
      if (reqId === requestRef.current) setIsLoading(false);
    }
  }, [search, currentPage, rowsPerPage, sort]);

  // Debounce typing so each keystroke is not a request.
  useEffect(() => {
    const t = setTimeout(() => setSearch(filterText.trim()), 300);
    return () => clearTimeout(t);
  }, [filterText]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    gtcApi
      .listIncoterms()
      .then(setIncoterms)
      .catch(err => notify.apiError(err, 'load incoterm'));
  }, []);

  const defaultIncotermId = () =>
    (
      incoterms.find(i => String(i.name).toUpperCase() === 'FOB') ||
      incoterms[0]
    )?.id ?? null;

  // A new search starts from the first page, otherwise a narrow match on a
  // later page looks like "no results".
  const handleFilterChange = e => {
    setFilterText(e.target.value);
    setCurrentPage(1);
  };

  const clearFilter = () => {
    setFilterText('');
    setCurrentPage(1);
  };

  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
  }, []);

  const handlePerRowsChange = useCallback(newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  }, []);

  const handleSort = (column, direction) => {
    setSort({ field: column.id, desc: direction === 'desc' });
    setCurrentPage(1);
  };

  /* ---------------- add / edit ---------------- */

  const openModal = () => {
    setForm({ ...EMPTY_FORM, incotermId: defaultIncotermId() });
    setEditing(null);
    setErrors({});
    setModalOpen(true);
  };

  const openEdit = row => {
    setForm({
      title: row.title || '',
      year: row.year ? String(row.year) : EMPTY_FORM.year,
      month: row.month ? String(row.month) : EMPTY_FORM.month,
      incotermId: row.incotermId ?? null,
      description: row.description || '',
      isActive: Boolean(row.isActive),
    });
    setEditing(row);
    setErrors({});
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setErrors({});
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  // Published content is locked: only Active may change.
  const locked = editing?.publishStatus === STATUS_PUBLISH.PUBLISH;

  const handleSave = async () => {
    const nextErrors = validateForm(form, GTC_SCHEMA);
    setErrors(nextErrors);
    if (hasErrors(nextErrors)) {
      focusFirstError(nextErrors, ['title']);
      return;
    }
    const payload = {
      title: form.title.trim(),
      year: Number(form.year),
      month: Number(form.month),
      description: (form.description || '').trim(),
      isActive: form.isActive,
      incotermId: form.incotermId || null,
    };
    setSaving(true);
    try {
      if (editing) {
        await gtcApi.update(
          editing.id,
          locked ? { isActive: payload.isActive } : payload
        );
        notify.success('GTC updated successfully.');
        setModalOpen(false);
        loadData();
        return;
      }
      // New GTC goes straight to its content builder.
      const created = await gtcApi.create(payload);
      notify.success('GTC added successfully.');
      setModalOpen(false);
      if (created?.id) navigate(`/apps-gtc-view/dtl/${created.id}`);
      else loadData();
    } catch (err) {
      notify.apiError(err, 'save GTC');
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- table ---------------- */

  const columns = [
    {
      id: 'gtcNumber',
      name: 'ID GTC',
      selector: row => row.gtcNumber,
      sortable: true,
      width: '9%',
      cell: row => (
        <span className='gtc-id' title={String(row.gtcNumber ?? '')}>
          {row.gtcNumber ?? '-'}
        </span>
      ),
    },
    {
      id: 'title',
      name: 'GTC Title',
      selector: row => row.title,
      sortable: true,
      width: '33%',
      cell: row => (
        <div className='gtc-title-cell'>
          <span
            className='gtc-title-cell__title'
            title={String(row.title ?? '')}
          >
            {row.title ?? '-'}
          </span>
          <span
            className='gtc-title-cell__desc'
            title={String(row.description || '')}
          >
            {row.description || '-'}
          </span>
        </div>
      ),
    },
    {
      id: 'year',
      name: 'Year',
      selector: row => row.year,
      sortable: true,
      width: '8%',
      center: true,
      cell: row => <span>{row.year ?? '-'}</span>,
    },
    {
      id: 'month',
      name: 'Month',
      selector: row => row.month,
      sortable: true,
      width: '10%',
      center: true,
      cell: row => <span>{monthName(row.month) || '-'}</span>,
    },
    {
      id: 'incotermName',
      name: 'Incoterm',
      selector: row => row.incotermName,
      sortable: true,
      width: '10%',
      center: true,
      cell: row => (
        <span
          className={`gtc-incoterm-badge ${(row.incotermName || '').toLowerCase()}`}
        >
          {row.incotermName || '-'}
        </span>
      ),
    },
    {
      id: 'isActive',
      name: 'Status',
      selector: row => row.isActive,
      sortable: true,
      width: '10%',
      center: true,
      cell: row => {
        const label = row.isActive ? 'Active' : 'Inactive';
        return <StatusBadge label={label} tone={STATUS_TONE[label]} />;
      },
    },
    {
      id: 'publishStatus',
      name: 'Status Publish',
      selector: row => row.publishStatus,
      sortable: true,
      width: '10%',
      center: true,
      cell: row => (
        <StatusBadge
          label={publishLabel(row)}
          tone={PUBLISH_TONE[publishLabel(row)]}
        />
      ),
    },
    {
      name: <span className='text-center w-100'>Action</span>,
      width: '10%',
      center: true,
      cell: row => (
        <div className='d-flex gap-1 justify-content-center w-100'>
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-eye-line'
            label='View Detail'
            onClick={() => navigate(`/apps-gtc-view/dtl/${row.id}`)}
          />
          <Can action='canUpdate'>
            <ActionButton
              variant='icon'
              size='sm'
              icon='ri-edit-2-line'
              label='Edit'
              onClick={() => openEdit(row)}
            />
          </Can>
        </div>
      ),
    },
  ];

  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  const filterChips = [
    filterText && {
      key: 'search',
      label: 'Search',
      value: filterText,
      onRemove: clearFilter,
    },
  ].filter(Boolean);

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <div className='to-page email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4'>
                <div className='to-hero mb-4'>
                  <PageBreadcrumb
                    fallback={{ label: 'Master GTC', to: '/apps-gtc-view' }}
                  />

                  <div className='d-flex align-items-center gap-3 flex-wrap'>
                    <span className='to-hero-icon'>
                      <i className='ri-file-text-line' aria-hidden='true' />
                    </span>
                    <div className='flex-grow-1'>
                      <h4 className='to-hero-title'>MASTER GTC</h4>
                      <p className='to-hero-subtitle'>
                        Manage the procurement General Terms and Conditions
                        (GTC) documents.
                      </p>
                    </div>
                    <div className='d-flex gap-2 flex-wrap'>
                      <button
                        type='button'
                        className='to-hero-btn'
                        onClick={loadData}
                      >
                        <i className='ri-refresh-line' aria-hidden='true' />
                        Refresh
                      </button>
                      <Can action='canCreate'>
                        <button
                          type='button'
                          className='to-hero-btn to-hero-btn--solid'
                          onClick={openModal}
                        >
                          <i className='ri-add-line' aria-hidden='true' />
                          Add GTC
                        </button>
                      </Can>
                    </div>
                  </div>
                </div>

                <div className='to-panel'>
                  <div className='to-toolbar'>
                    <ListSearch
                      value={filterText}
                      onChange={handleFilterChange}
                      placeholder='Search GTC by id, title, or description...'
                      ariaLabel='Search GTC'
                    />
                    <ClearFiltersButton
                      count={filterChips.length}
                      onClick={clearFilter}
                    />
                    <span className='to-toolbar-meta'>
                      {filterText
                        ? `${totalRows} match${totalRows === 1 ? '' : 'es'} found`
                        : `Showing ${startEntry}–${endEntry} of ${totalRows} GTC`}
                    </span>
                  </div>

                  <ActiveFilterChips chips={filterChips} />

                  <div className='to-table-wrap'>
                    <DataTable
                      columns={columns}
                      data={rows}
                      onSort={handleSort}
                      sortServer
                      highlightOnHover
                      persistTableHead
                      progressPending={isLoading}
                      progressComponent={<SkeletonRows />}
                      noDataComponent={
                        <EmptyState
                          title='No GTC found'
                          emptyHint='No GTC yet. Click "Add GTC" to create one.'
                          filteredHint={`No GTC matches "${filterText}".`}
                          hasFilters={Boolean(filterText)}
                          onClear={clearFilter}
                          action={
                            canCreate
                              ? { label: 'Add GTC', onClick: openModal }
                              : null
                          }
                        />
                      }
                      customStyles={GTC_TABLE_STYLES}
                    />
                  </div>

                  <ListPager
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalRows={totalRows}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handlePerRowsChange}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Add / Edit */}
          <Modal
            isOpen={modalOpen}
            role='dialog'
            autoFocus={true}
            centered
            id='gtcModal'
            toggle={closeModal}
            data-bs-backdrop='static'
            size='md'
            className='gtc-modal'
          >
            <ModalHeader toggle={closeModal} className='gtc-modal__header'>
              <div className='gtc-modal__header-left'>
                <span className='gtc-modal__icon'>
                  <i className='ri-file-text-line' aria-hidden='true' />
                </span>
                <div className='gtc-modal__heading'>
                  <div className='gtc-modal__title'>
                    {editing ? 'Edit GTC' : 'Add GTC'}
                  </div>
                  <div className='gtc-modal__subtitle'>
                    {editing
                      ? 'Update the master General Terms & Conditions document'
                      : 'Add a new master General Terms & Conditions document'}
                  </div>
                </div>
              </div>
            </ModalHeader>

            <ModalBody>
              <div className='gtc-form'>
                {locked && (
                  <div className='alert alert-info py-2 fs-12 mb-0'>
                    This GTC is published. Only the GTC Status can be changed.
                  </div>
                )}
                <TextField
                  label='GTC Title'
                  name='title'
                  required
                  mode={locked ? 'disabled' : 'editable'}
                  placeholder='e.g. Pertamina Patra Niaga GTC 2026 Edition'
                  value={form.title}
                  error={errors.title}
                  onChange={handleChange}
                />
                <FormGrid columns={2}>
                  <SelectField
                    label='Year'
                    name='year'
                    mode={locked ? 'disabled' : 'editable'}
                    options={YEAR_OPTIONS}
                    value={form.year}
                    onChange={value =>
                      setForm(prev => ({ ...prev, year: value }))
                    }
                  />
                  <SelectField
                    label='Month'
                    name='month'
                    mode={locked ? 'disabled' : 'editable'}
                    options={MONTH_OPTIONS}
                    value={form.month}
                    onChange={value =>
                      setForm(prev => ({ ...prev, month: value }))
                    }
                  />
                </FormGrid>
                <div className='hy-field' data-field='incoterm'>
                  <span className='hy-field__label'>INCOTERM</span>
                  <div className='gtc-incoterm-buttons'>
                    {incoterms.length === 0 && (
                      <span className='text-muted fs-12'>
                        No incoterm available.
                      </span>
                    )}
                    {incoterms.map(term => (
                      <button
                        key={term.id}
                        type='button'
                        disabled={locked}
                        className={
                          form.incotermId === term.id
                            ? 'gtc-incoterm-btn gtc-incoterm-btn--active'
                            : 'gtc-incoterm-btn gtc-incoterm-btn--inactive'
                        }
                        onClick={() =>
                          setForm(prev => ({ ...prev, incotermId: term.id }))
                        }
                      >
                        {term.name}
                      </button>
                    ))}
                  </div>
                </div>
                <TextAreaField
                  label='GTC Description'
                  name='description'
                  rows={4}
                  mode={locked ? 'disabled' : 'editable'}
                  placeholder='Enter a short description of this GTC document...'
                  value={form.description}
                  onChange={handleChange}
                />
                <div className='hy-field' data-field='isActive'>
                  <span className='hy-field__label'>Status GTC</span>
                  <div className='gtc-status-card'>
                    <div>
                      <div className='gtc-status-card__title'>
                        {form.isActive ? 'Active' : 'Inactive'}
                      </div>
                      <div className='gtc-status-card__desc'>
                        {form.isActive
                          ? 'Active GTC can be used in procurements'
                          : 'Inactive GTC cannot be used in procurements'}
                      </div>
                    </div>
                    <div className='form-check form-switch'>
                      <Input
                        id='gtc-status-switch'
                        type='checkbox'
                        role='switch'
                        className='form-check-input'
                        checked={form.isActive}
                        onChange={e =>
                          setForm(prev => ({
                            ...prev,
                            isActive: e.target.checked,
                          }))
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </ModalBody>

            <ModalFooter>
              <ActionButton
                variant='secondary'
                size='sm'
                icon='ri-close-line'
                onClick={closeModal}
              >
                Cancel
              </ActionButton>
              <ActionButton
                variant='primary'
                size='sm'
                icon='ri-save-line'
                disabled={!form.title.trim() || saving}
                onClick={handleSave}
              >
                Save GTC
              </ActionButton>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default GtcList;
